import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import type { AppSettings, MediaChannelOption, UpdateSettingsBody } from '@/types/api';

export function useSettingsQuery(): UseQueryResult<AppSettings, Error> {
  return useQuery<AppSettings, Error>({
    queryKey: queryKeys.settings(),
    queryFn: ({ signal }) => apiClient.getSettings(signal),
  });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation<AppSettings, Error, UpdateSettingsBody>({
    mutationFn: (body) => apiClient.updateSettings(body),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.settings(), settings);
      // Which channels are marked monitored is part of the options' answer.
      void queryClient.invalidateQueries({ queryKey: queryKeys.mediaChannelOptions() });
    },
  });
}

/** Channels the media picker offers: everywhere SLC has seen links, images or videos. */
export function useMediaChannelOptions(): UseQueryResult<MediaChannelOption[], Error> {
  return useQuery<MediaChannelOption[], Error>({
    queryKey: queryKeys.mediaChannelOptions(),
    queryFn: async ({ signal }) => (await apiClient.listMediaChannelOptions(signal)).items,
  });
}
